/**
 * Updates product page price when Simple Bundles infinite options change.
 */
(function () {
  const BUNDLE_ROOT_SELECTORS = [
    '[data-simple-bundles-options]',
    '#simple-bundles-options',
    '#data-simple-bundles-options'
  ];

  let priceConfig = null;
  let isBound = false;
  let updateTimer = null;

  function getBundleRoot() {
    for (const selector of BUNDLE_ROOT_SELECTORS) {
      const element = document.querySelector(selector);
      if (element) return element;
    }
    return null;
  }

  function loadPriceConfig() {
    const configEl = document.getElementById('simple-bundles-price-config');
    if (!configEl) return null;
    try {
      const config = JSON.parse(configEl.textContent);
      if (typeof config.basePrice !== 'number' || !config.moneyFormat) return null;
      config.baseCompareAtPrice = Number(config.baseCompareAtPrice) || 0;
      return config;
    } catch (error) {
      return null;
    }
  }

  function formatMoney(cents, format) {
    if (!format || typeof format !== 'string') return '';

    const amount = Number(cents) / 100;
    if (Number.isNaN(amount)) return '';

    const hasDecimals = !format.includes('amount_no_decimals');
    const usesCommaSeparator = format.includes('amount_with_comma_separator');

    const fixed = amount.toFixed(hasDecimals ? 2 : 0);
    const parts = fixed.split('.');
    let dollars = parts[0];
    const decimals = parts[1] || '';

    if (usesCommaSeparator) {
      dollars = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    const amountStr = hasDecimals ? `${dollars}.${decimals}` : dollars;
    const amountNoDecimals = dollars.replace(/,/g, '');
    const amountWithCommaSeparator = usesCommaSeparator ? amountStr : amountStr;

    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithCommaSeparator)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amountStr);
  }

  function getOptionPriceDiffCents(optionEl) {
    if (!optionEl || !optionEl.value) return 0;

    const dataDiff = parseFloat(optionEl.dataset.priceDiff);
    if (!Number.isNaN(dataDiff) && dataDiff !== 0) {
      return Math.round(dataDiff * 100);
    }

    for (const text of [optionEl.value, optionEl.textContent]) {
      const match = String(text || '').match(/\(\s*\+\s*\$?([\d,.]+)\s*\)/i);
      if (!match) continue;

      const amount = parseFloat(match[1].replace(/,/g, ''));
      if (!Number.isNaN(amount)) return Math.round(amount * 100);
    }

    return 0;
  }

  function getSelectedDiffCents(root) {
    return Array.from(root.querySelectorAll('select')).reduce((total, select) => {
      const option = select.options[select.selectedIndex];
      return total + getOptionPriceDiffCents(option);
    }, 0);
  }

  function updateDisplayedPrices(totalCents, compareAtCents) {
    if (!priceConfig) return;

    const formattedPrice = formatMoney(totalCents, priceConfig.moneyFormat);
    if (!formattedPrice) return;

    const hasDiscount = compareAtCents > totalCents;
    const formattedCompare = hasDiscount ? formatMoney(compareAtCents, priceConfig.moneyFormat) : '';
    const savingsCents = hasDiscount ? compareAtCents - totalCents : 0;
    const savingsPercent = hasDiscount ? Math.round((savingsCents / compareAtCents) * 100) : 0;
    const formattedSavings = hasDiscount ? formatMoney(savingsCents, priceConfig.moneyFormat) : '';

    document.querySelectorAll('.js-price-current, .product-info__price .price__current, [data-sticky-atc-price] .price__current').forEach((el) => {
      el.textContent = formattedPrice;
    });

    const priceWithSavings = document.querySelector('.price-with-savings');
    if (!priceWithSavings) return;

    const compareEl = priceWithSavings.querySelector('.js-price-compare');
    const savingsEl = priceWithSavings.querySelector('.js-price-savings');
    const compareRow = priceWithSavings.querySelector('.price-with-savings__row:nth-child(2)');

    if (compareEl) compareEl.textContent = formattedCompare;
    if (savingsEl) {
      savingsEl.textContent = hasDiscount
        ? `Save ${savingsPercent}% (${formattedSavings})`
        : '';
    }
    if (compareRow) compareRow.hidden = !hasDiscount;
  }

  function updateBundlePrice() {
    if (!priceConfig) return;

    const root = getBundleRoot();
    if (!root) return;

    try {
      const diffCents = getSelectedDiffCents(root);
      const totalCents = priceConfig.basePrice + diffCents;
      const compareAtCents = priceConfig.baseCompareAtPrice + diffCents;
      updateDisplayedPrices(totalCents, compareAtCents);
    } catch (error) {
      // Fail silently — never break the product page.
    }
  }

  function scheduleUpdate() {
    clearTimeout(updateTimer);
    updateTimer = setTimeout(updateBundlePrice, 50);
  }

  function bindBundlePricing() {
    if (isBound) return true;

    const root = getBundleRoot();
    if (!root) return false;

    const productInfo = document.querySelector('.product-info');
    if (!productInfo) return false;

    productInfo.addEventListener('change', (event) => {
      if (!event.target.matches('select')) return;
      if (!event.target.closest('[data-simple-bundles-options], #simple-bundles-options, #data-simple-bundles-options')) return;
      scheduleUpdate();
    });

    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('on:variant:change', (event) => {
        const variant = event.detail?.variant;
        if (!variant) return;

        priceConfig.basePrice = variant.price;
        priceConfig.baseCompareAtPrice = variant.compare_at_price || 0;
        scheduleUpdate();
      });
    }

    isBound = true;
    scheduleUpdate();
    return true;
  }

  function initPricing() {
    priceConfig = loadPriceConfig();
    if (!priceConfig) return;

    if (bindBundlePricing()) return;

    const productInfo = document.querySelector('.product-info');
    if (!productInfo) return;

    const observer = new MutationObserver(() => {
      if (bindBundlePricing()) observer.disconnect();
    });

    observer.observe(productInfo, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPricing, { once: true });
  } else {
    initPricing();
  }
})();
